// Shared types, constants, and helpers for VisitReportPage and its section components.

export interface MedRow { drug_name: string; adherence_status: string; }

export interface FormState {
  bp_sys: string; bp_dia: string; bp_pos: string; hr: string;
  spo2: string; temp: string; glucose: string; weight: string; rr: string;
  // not-taken toggles ("yes" = not taken)
  bp_not_taken: string;    bp_not_taken_reason: string;
  hr_not_taken: string;    hr_not_taken_reason: string;
  spo2_not_taken: string;  spo2_not_taken_reason: string;
  temp_not_taken: string;  temp_not_taken_reason: string;
  glucose_not_taken: string; glucose_not_taken_reason: string;
  weight_not_taken: string;  weight_not_taken_reason: string;
  rr_not_taken: string;    rr_not_taken_reason: string;
  // context fields (shown when respective vital is taken)
  hr_rhythm: string; glucose_timing: string; ankle_oedema: string;
  mobility: string; fall_visit: string; fall_since: string;
  mobility_vs_last: string; fall_injury_sustained: string;
  near_miss: string; patient_safety_concern: string;
  fall_count_since_last_visit: string;
  fall_injury_description: string;
  escalation_severity: string;
  environment_safety_concern: boolean;
  home_condition_concern_type: string;
  medication_error_observed: boolean;
  wandering_behaviour_reported: boolean;
  medication_list_verified: boolean;
  pain_present: string; pain_severity: string; pain_location: string;
  pain_vs_last: string;
  // Functional Status (DRS B3)
  ambulation: string; adl_score: string;
  continence: string; grooming: string; med_admin_cap: string;
  // Cognitive Assessment
  cognitive: string; mood: string; appetite: string;
  cognitive_vs_last: string;
  cog_test_used: string; cog_test_score: string; cog_interpretation: string;
  breathlessness_present: string; breathlessness_severity: string;
  breathlessness_timing: string; eating_status: string;
  fluid_intake: string; sleep_quality: string;
  bowel_function: string; urinary_function: string;
  skin_condition: string; self_care_ability: string;
  communication_ability: string;
  // Symptom flags (DRS B4)
  unplanned_weight_loss_reported: string; fever_or_night_sweats: string;
  persistent_cough: string; dizziness_reported: string;
  nausea_or_vomiting: string; unusual_fatigue_reported: string;
  symptom_detail: Record<string, string>;
  patient_engagement_level: string;
  overall_status: string; escalation: boolean;
  notes: string; recommendations: string[];
  meds: MedRow[];
}

type BooleanFields = "escalation" | "medication_error_observed" | "wandering_behaviour_reported" | "environment_safety_concern" | "medication_list_verified";
type StringFields = keyof Omit<FormState, "meds" | "recommendations" | "symptom_detail" | BooleanFields>;
export type Action =
  | { field: StringFields; value: string }
  | { field: BooleanFields; value: boolean }
  | { field: "recommendations"; value: string[] }
  | { field: "meds"; value: MedRow[] }
  | { field: "symptom_detail"; value: Record<string, string> };

export const INIT: FormState = {
  bp_sys: "", bp_dia: "", bp_pos: "", hr: "", spo2: "", temp: "", glucose: "", weight: "", rr: "",
  bp_not_taken: "", bp_not_taken_reason: "",
  hr_not_taken: "", hr_not_taken_reason: "",
  spo2_not_taken: "", spo2_not_taken_reason: "",
  temp_not_taken: "", temp_not_taken_reason: "",
  glucose_not_taken: "", glucose_not_taken_reason: "",
  weight_not_taken: "", weight_not_taken_reason: "",
  rr_not_taken: "", rr_not_taken_reason: "",
  hr_rhythm: "", glucose_timing: "", ankle_oedema: "",
  mobility: "", fall_visit: "no", fall_since: "no",
  mobility_vs_last: "same", fall_injury_sustained: "no",
  near_miss: "no", patient_safety_concern: "no",
  fall_count_since_last_visit: "", fall_injury_description: "",
  escalation_severity: "", environment_safety_concern: false,
  home_condition_concern_type: "", medication_error_observed: false,
  wandering_behaviour_reported: false, medication_list_verified: false,
  pain_present: "no", pain_severity: "", pain_location: "",
  pain_vs_last: "same",
  ambulation: "", adl_score: "",
  continence: "", grooming: "", med_admin_cap: "",
  cognitive: "", mood: "", appetite: "",
  cognitive_vs_last: "same",
  cog_test_used: "not_done", cog_test_score: "", cog_interpretation: "",
  breathlessness_present: "no", breathlessness_severity: "",
  breathlessness_timing: "", eating_status: "",
  unplanned_weight_loss_reported: "no", fever_or_night_sweats: "no",
  persistent_cough: "no", dizziness_reported: "no",
  nausea_or_vomiting: "no", unusual_fatigue_reported: "no",
  symptom_detail: {},
  fluid_intake: "", sleep_quality: "",
  bowel_function: "", urinary_function: "",
  skin_condition: "", self_care_ability: "",
  communication_ability: "",
  patient_engagement_level: "", overall_status: "", escalation: false,
  notes: "", recommendations: [],
  meds: [{ drug_name: "", adherence_status: "compliant_all_doses" }],
};

export const SECTION = "bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4";
export const HEADING = "text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3";

export const numOrNull = (s: string): number | null => (s === "" ? null : Number(s));

export function set<K extends keyof FormState>(f: FormState, field: K, value: FormState[K]): FormState {
  return { ...f, [field]: value };
}
